import Button from "components/base/Button";
import SearchBar from "components/base/SearchBar";
import { useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { useSplit } from "./SplitContext";
import { getCbm, matchesProduct } from "./utils";

function TargetShipmentCard({ target, index }) {
  const {
    rowById,
    targets,
    selectedRowIds,
    selectedCartonsAreValid,
    selectedCbm,
    assignSelectedRows,
    removeAllocation,
    removeTarget,
  } = useSplit();

  // These UI states belong to one target card only.
  const [searchText, setSearchText] = useState("");
  const [rowsExpanded, setRowsExpanded] = useState(false);

  // Data shown by this target shipment card.
  const targetRows = target.allocations
    .map((allocation) => {
      const row = rowById.get(allocation.shipmentInvoiceRowId);
      return row ? { ...allocation, row } : null;
    })
    .filter(Boolean);
  const totalCbm = targetRows.reduce(
    (total, { row, cartonCount }) => total + getCbm(row, cartonCount),
    0,
  );
  const visibleTargetRows = targetRows.filter(({ row }) =>
    matchesProduct(row, searchText),
  );

  return (
    <article className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-800">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-y-1">
            <div className="w-36 shrink-0 text-sm font-bold sm:w-48">
              Shipment {index + 1}
            </div>
            <div className="shrink-0 whitespace-nowrap pl-3 text-sm">
              <span className="font-bold">{totalCbm.toFixed(3)}</span> m³
            </div>
            {selectedRowIds.length > 0 && selectedCartonsAreValid ? (
              <div className="shrink-0 whitespace-nowrap text-sm">
                <span className="mx-2 opacity-60">→</span>
                <span className="font-bold text-green-600 dark:text-green-500">
                  {(totalCbm + selectedCbm).toFixed(3)}
                </span>{" "}
                m³
              </div>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <SearchBar
              containerClassName="w-36 shrink-0 sm:w-48"
              value={searchText}
              onChange={(value) => {
                setSearchText(value);
                if (value.trim()) setRowsExpanded(true);
              }}
              placeholder="Search product"
            />
            <Button
              className="shrink-0 !space-x-1 text-sm"
              icon={
                <FiChevronRight
                  className={`transition-transform ${
                    rowsExpanded ? "rotate-90" : ""
                  }`}
                  size={16}
                />
              }
              onClick={() => setRowsExpanded((current) => !current)}
            >
              {rowsExpanded ? "Hide products" : "Show products"}
            </Button>
          </div>
        </div>
        <Button
          danger
          className="self-start"
          disabled={targets.length === 1 || targetRows.length > 0}
          onClick={() => removeTarget(target.id)}
        >
          Remove
        </Button>
      </div>

      {/* Allocated products. */}
      {rowsExpanded ? (
        <div className="min-h-12 max-h-[30vh] divide-y divide-gray-100 overflow-y-auto px-4 dark:divide-gray-800">
          {visibleTargetRows.length ? (
            visibleTargetRows.map(({ row, cartonCount }) => (
              <div
                key={row.id}
                className="-mx-4 flex items-start gap-3 px-6 py-4 font-normal transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <div className="grid min-w-0 flex-1 grid-cols-[8rem_minmax(0,1fr)] items-start gap-x-4 gap-y-2 text-sm">
                  <div className="col-start-1 row-start-1 break-all font-bold">
                    {row.itemNumber}
                  </div>
                  <div className="col-start-2 row-start-1 min-w-0 break-words">
                    {row.productName}
                  </div>
                  <div className="col-start-1 row-start-2">
                    <span className="font-bold">
                      {getCbm(row, cartonCount).toFixed(3)}
                    </span>{" "}
                    m³
                  </div>
                  <div className="col-start-2 row-start-2">
                    <span className="font-bold">
                      {cartonCount * Number(row.qtyPerCarton)}
                    </span>{" "}
                    / {row.qtyPerCarton} ={" "}
                    <span className="font-bold">{cartonCount}</span> cartons
                  </div>
                </div>
                <Button
                  className="h-8 w-8 justify-center rounded-md p-0 text-lg hover:bg-red-50 dark:hover:bg-red-950/30"
                  danger
                  onClick={() => removeAllocation(target.id, row.id)}
                >
                  ×
                </Button>
              </div>
            ))
          ) : targetRows.length ? (
            <div className="py-4 text-sm opacity-60">No matching products.</div>
          ) : (
            <div className="py-4 text-sm opacity-60">No products assigned.</div>
          )}
        </div>
      ) : null}

      <div className="flex items-center gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-800">
        <Button
          className="rounded-md px-2 py-1 text-sm transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
          disabled={selectedRowIds.length === 0 || !selectedCartonsAreValid}
          onClick={() => assignSelectedRows(target.id)}
        >
          Assign selected
        </Button>
      </div>
    </article>
  );
}

export default function TargetShipments() {
  const { targets, addTarget } = useSplit();

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">New shipments</h2>
        <Button onClick={addTarget}>+ Shipment</Button>
      </div>
      {targets.map((target, index) => (
        <TargetShipmentCard key={target.id} target={target} index={index} />
      ))}
    </section>
  );
}
